import { z } from 'zod';
import { CharacterTypeEnum, ShortcutsSchema, SceneObjectSchema, Model3DMaterialOverrideSchema } from './character';
/**
 * Public-info response shape — what `GET /api/characters/:id/public-info`
 * returns to ANY browser (no auth required). Strictly a subset of the
 * character record: every field below is safe to expose to attackers.
 *
 * Forbidden by design (asserted in `manifest.test.ts` deep-walk):
 *   - Any apiKey / credential
 *   - Any upstream URL (`ai.baseUrl`, `voice.voxcpm.baseUrl`,
 *     `voice.asr.baseUrl`) — these reveal SSRF-able endpoints AND
 *     leak operational topology
 *   - The full `agenticGraph` block (already hidden — system prompts +
 *     tool wiring is admin-only IP)
 *   - The persisted `voice.voxcpm.{voice, style, instructText}` —
 *     exposed only via the `voice.provider === 'voxcpm'` flag; the
 *     embed page re-fetches voice/style options through the
 *     character-keyed admin-api proxy when needed.
 *
 * The `voice` sub-block is a "capability summary": tells the embed
 * page what UI controls are appropriate (browser vs voxcpm, mic on/off)
 * without surfacing how those capabilities are wired upstream.
 */
export const PublicCharacterInfoSchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    type: CharacterTypeEnum,
    pngtuberV2Manifest: z.unknown().nullable(),
    model3d: z
        .object({
        modelPath: z.string(),
        modelFormat: z.enum(['glb', 'fbx']).optional(),
        mouthBinding: z.unknown().optional(),
        animationMap: z.record(z.string(), z.string()).optional(),
        proceduralActions: z.array(z.unknown()).optional(),
        texturePack: z.unknown().optional(),
        morphPack: z.unknown().optional(),
        expressionMode: z.enum(['texturePack', 'morphTargets']).optional(),
        clipLabels: z.record(z.string(), z.string()).optional(),
        defaultClipLabels: z.array(z.string()).optional(),
        oneShotClipLabels: z.array(z.string()).optional(),
        renderParams: z
            .object({
            mouthFadeSpeed: z.number().optional(),
            mouthOpacity: z.number().optional(),
            blinkMinInterval: z.number().optional(),
            blinkMaxInterval: z.number().optional(),
            blinkDuration: z.number().optional(),
            expressionFadeDuration: z.number().optional(),
            visemeDriftRate: z.number().optional(),
        })
            .optional(),
        viewportPose: z.object({ rotationY: z.number().optional() }).optional(),
        autoReturnToNature: z.boolean().optional(),
        materialOverrides: z.array(Model3DMaterialOverrideSchema).max(80).optional(),
        // embed-thinking-visual-feedback: visual cues during the
        // LLM-thinking gap. All optional; absent = degraded UX path
        // (chat-bubble dots + pill text only, no avatar change).
        thinkingExpression: z.string().optional(),
        thinkingClip: z.string().optional(),
        thinkingSoundUrl: z.string().url().optional(),
        // scene-objects-and-attachments: auxiliary 3D models loaded
        // alongside the character. Mirrors SceneObjectSchema 1:1.
        sceneObjects: z.array(SceneObjectSchema).max(16).optional(),
    })
        .nullable(),
    hasRig: z.boolean(),
    hasV1Manifest: z.boolean(),
    viewport: z.object({
        width: z.number().int().positive(),
        height: z.number().int().positive(),
    }),
    /**
     * Capability summary — what voice surfaces does this character offer?
     * Browser uses these flags to decide which UI to render.
     *
     * `provider: 'voxcpm'` means "this character speaks via VoxCPM2"; the
     * embed page POSTs to `/api/tts-voxcpm/:characterId/synthesize` and
     * the server resolves the upstream URL from Cosmos.
     *
     * `asrEnabled: true` means "render the mic button"; no upstream
     * details accompany the flag.
     */
    /**
     * Capability summary — what voice surfaces does this character offer?
     *
     * `provider` is the legacy public-safe enum kept for back-compat
     * (drives the legacy speak() path). `tts.provider` is the new
     * voice-conversation-mode enum that drives the embed page's
     * voice-conversation relay (`/api/voice/*`). Both flags reach the
     * browser; the upstream URL / apiKey behind either provider stay
     * server-side ONLY.
     */
    voice: z.object({
        provider: z.enum(['browser', 'voxcpm', 'voai', 'brainserver']),
        asrEnabled: z.boolean(),
        tts: z.object({
            provider: z.enum(['voxcpm2', 'voai', 'brainserver', 'browser', 'none']),
        }),
        /**
         * Per `embed-mid-reply-interrupt` spec — surfaced in public-info
         * so the embed bundle can read the per-character toggle without
         * an extra fetch. Defaults applied at publicInfoHandler so
         * absent records still get sensible behaviour.
         */
        interruptEnabled: z.boolean(),
        interruptHoldMs: z.number().int().min(100).max(2000),
        /**
         * Per embed-mid-reply-interrupt: keyword list (may be empty).
         * When empty, the embed treats the call as strict turn-taking
         * — user speech during AI playback is silently dropped. When
         * non-empty, only partials containing one of these substrings
         * trigger an abort.
         */
        interruptKeywords: z.array(z.string()),
        /**
         * Per `embed-idle-small-talk` spec — surfaced in public-info so
         * the embed bundle can read the per-character settings without an
         * extra fetch. Defaults applied at publicInfoHandler so absent
         * records still get sensible behaviour. `idleEnabled: false`
         * disables the entire idle nudge state machine.
         */
        idleEnabled: z.boolean(),
        idleStage1Ms: z.number().int().min(5000).max(60000),
        idleStage2Ms: z.number().int().min(5000).max(60000),
        idleStage3Ms: z.number().int().min(5000).max(120000),
        /**
         * Per `character-custom-audio-clips` Trigger B — when set, the
         * stage plays the referenced clip instead of running the
         * LLM-driven idle nudge. Resolved client-side against
         * `audioClips[].id`.
         */
        idleStage1ClipId: z.string().uuid().optional(),
        idleStage2ClipId: z.string().uuid().optional(),
        idleStage3ClipId: z.string().uuid().optional(),
    }),
    /**
     * Per `character-custom-audio-clips`: clip catalog the embed
     * widget needs to drive Trigger A (AI structured-output) playback,
     * Trigger B (idle clip), Trigger C (greeting clip), Trigger D
     * (keyword response clip). Transcript + aiVisible are stripped —
     * server-side only details.
     */
    audioClips: z.array(z.object({
        id: z.string().uuid(),
        url: z.string().url(),
        contentType: z.enum(['audio/mpeg', 'audio/wav', 'audio/ogg']),
        durationSec: z.number().positive().optional(),
    })).default([]),
    /**
     * Per `character-custom-audio-clips` Trigger C — greeting on
     * widget mount. Absent / `enabled: false` → no greeting fires.
     */
    greeting: z.object({
        enabled: z.boolean(),
        text: z.string(),
        clipId: z.string().uuid().optional(),
    }).optional(),
    /**
     * Per `character-custom-audio-clips` Trigger D — keyword short-
     * circuit catalog. Scan happens SERVER-SIDE inside the chat
     * handler; this list is surfaced so the embed widget can render a
     * preview / hint UI (none yet in v1), NOT so the browser scans
     * locally. Empty array when no entries configured.
     */
    keywordResponses: z.array(z.object({
        id: z.string().uuid(),
        keyword: z.string(),
    })).default([]),
    /**
     * Per operator-customisable embed colors (2026-05-14): chat bubble
     * + input field colors. Each optional; embed runtime falls back to
     * its built-in defaults when an entry is missing. Lives in
     * public-info so the runtime reads it via its existing fetch path
     * (no extra config.json round-trip needed).
     */
    embedColors: z.object({
        aiBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        aiBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        userBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        userBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        inputBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
        inputText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    }).optional(),
    /**
     * Per embed-conversation-shortcuts spec — optional quick-reply FAB
     * configuration. Absent → embed renders no FAB. Mirrors the
     * persisted ShortcutsSchema 1:1; descriptions are exposed publicly
     * (operators must treat them as visitor-readable content).
     */
    shortcuts: ShortcutsSchema.optional(),
    /**
     * How many of the most recent chat turns the embed sends back as
     * `history` for context. The full `ai` block stays server-side
     * (apiKey + baseUrl + provider are secrets) — only this single
     * non-sensitive integer is surfaced.
     */
    contextWindowMessages: z.number().int().min(0).max(50),
});
